import { useState } from "react";
import { Link } from "react-router-dom";
import type { FreePost } from "@/lib/types";
import Avatar from "@/components/base/Avatar";
import Modal from "@/components/base/Modal";
import { apiPost } from "@/lib/api";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";
import { mediaUrl } from "@/lib/media";

interface FreeCardProps {
  post: FreePost;
  onDeleted?: () => void;
}

export function formatRelative(value?: string): string {
  if (!value) return "";
  const str = String(value).replace(" ", "T");
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return value;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "방금 전";
  if (min < 60) return `${min}분 전`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}시간 전`;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  if (y === now.getFullYear()) return `${m}.${day}`;
  return `${y}.${m}.${day}`;
}

function formatDate(value?: string): string {
  if (!value) return "";
  const str = String(value).replace(" ", "T");
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

interface LikeState {
  is_liked: number;
  like_cnt: number;
}

// 자유글 목록 카드
export default function FreeCard({ post, onDeleted }: FreeCardProps) {
  const { requireLogin } = useAuth();
  const { toast } = useToast();

  const [likeState, setLikeState] = useState<LikeState | null>(null);
  const [likeBusy, setLikeBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const w = post.writer;
  const writerName =
    [w?.name, w?.aho].filter(Boolean).join(" ") ||
    [post.name, post.aho].filter(Boolean).join(" ");
  const photo = w?.photo || post.photo;
  const bizName = w?.biz_name || post.biz_name;
  const firstImage = post.images?.[0];

  const isLiked = (likeState?.is_liked ?? post.is_liked ?? 0) === 1;
  const likeCnt = likeState?.like_cnt ?? post.like_cnt ?? 0;
  const regdate = formatDate(post.regdate || post.reg_date);

  const toggleLike = async () => {
    if (!requireLogin()) return;
    if (likeBusy) return;
    setLikeBusy(true);
    const prev: LikeState = {
      is_liked: isLiked ? 0 : 1,
      like_cnt: Math.max(0, likeCnt + (isLiked ? -1 : 1)),
    };
    setLikeState(prev);
    try {
      const res = await apiPost<{ is_liked: number; like_cnt: number }>(
        "free_write.php",
        { act: "like", no: post.no }
      );
      const d = res.data;
      if (d && typeof d.like_cnt === "number") {
        setLikeState({
          is_liked: d.is_liked ?? prev.is_liked,
          like_cnt: d.like_cnt,
        });
      }
    } catch (e) {
      setLikeState({ is_liked: post.is_liked ?? 0, like_cnt: post.like_cnt ?? 0 });
      toast(e instanceof Error ? e.message : "처리하지 못했습니다.", "error");
    } finally {
      setLikeBusy(false);
    }
  };

  const deletePost = async () => {
    setDeleting(true);
    try {
      await apiPost("free_write.php", { act: "delete", no: post.no });
      toast("삭제되었습니다.", "success");
      setDeleteOpen(false);
      onDeleted?.();
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <article className="relative rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 md:p-5">
        {post.is_mine === 1 && (
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            aria-label="글 삭제"
            className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-accent-50 hover:text-accent-700"
          >
            <i className="ri-delete-bin-line" aria-hidden="true"></i>
          </button>
        )}

        <Link to={`/free/${post.no}`} className="group block">
          <div className="flex items-center gap-2.5 pr-10">
            <Avatar name={writerName || "회원"} photo={photo} size={36} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground-800">
                {writerName || "회원"}
              </p>
              <p className="text-xs text-foreground-400">
                {bizName ? `${bizName} · ` : ""}
                {formatRelative(post.regdate || post.reg_date)}
              </p>
            </div>
          </div>

          <h3 className="mt-3 line-clamp-1 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
            {post.subject || "제목 없음"}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{post.content}</p>

          {firstImage && (
            <div className="mt-3 h-44 w-full overflow-hidden rounded-lg bg-background-100">
              <img
                src={mediaUrl(firstImage)}
                alt={post.subject || "첨부 이미지"}
                className="h-full w-full object-cover object-top"
              />
            </div>
          )}
        </Link>

        <div className="mt-3 flex items-center gap-5 border-t border-background-200 pt-3 text-sm text-foreground-500">
          <button
            type="button"
            onClick={toggleLike}
            disabled={likeBusy}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-70 ${
              isLiked ? "text-accent-600" : "hover:text-accent-600"
            }`}
          >
            <i
              className={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"}
              aria-hidden="true"
            ></i>
            {likeCnt}
          </button>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-chat-3-line" aria-hidden="true"></i>
            {post.cmt_cnt ?? 0}
          </span>
          <span className="ml-auto whitespace-nowrap text-xs text-foreground-400">{regdate}</span>
        </div>
      </article>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <div className="p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">글을 삭제할까요?</h2>
          <p className="mt-2 text-sm text-foreground-600">삭제한 글은 되돌릴 수 없습니다.</p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={deletePost}
              disabled={deleting}
              className="flex-1 whitespace-nowrap rounded-md bg-accent-600 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-accent-700 disabled:opacity-60"
            >
              {deleting ? "삭제 중..." : "삭제"}
            </button>
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              취소
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}